/**
 * @author mrdoob / http://mrdoob.com/ //Higharc edits
 */
import { DOMParser as ServerSideDOMParser } from "@xmldom/xmldom";
import type { DotsPerInch, SheetPixels } from "common/types/nominalNumbersTypes";
import { assertIsOneOf } from "common/utils/assertOfType";
import { sheetPixelsNew } from "common/utils/nominalNumbers";
import { TextVerticalAlignment } from "common/utils/textAlignment";
import type { Mat3 } from "geom/mat3";
import { mat3Copy, mat3CSSRotate, mat3Ident, mat3Mult, mat3Scale, mat3Translate } from "geom/mat3";
import { vec2Copy, type Vec2 } from "geom/vec2";

function isDocumentFragment(e: any): e is DocumentFragment {
  return e.nodeType === 11 /*Node.DOCUMENT_FRAGMENT_NODE*/;
}

function isDocument(e: any): e is Document {
  return e.nodeType === 9 /*Node.DOCUMENT_NODE*/;
}

export interface SVGPathStyle {
  fill?: string;
  fillOpacity?: number;
  opacity?: number;
  stroke?: string;
  strokeOpacity?: number;
  strokeWidth?: SheetPixels;
  strokeLineJoin?: string;
  strokeLineCap?: string;
  strokeMiterLimit?: number;
  strokeDashArray?: SheetPixels[];
  visibility?: string;
}

export interface SVGParserCallbacks<ShapePath, Path> {
  newShapePath(): ShapePath;
  newPath(): Path;

  setViewBox?(x: number, y: number, width: number, height: number): void;
  setSvgWidth?(width: number): void;
  setSvgHeight?(height: number): void;
  setStyle?(style: SVGPathStyle): void;

  // ShapePath related callbacks.
  // The points are passed to callbacks WITHOUT TRANSFORMATION!
  // It is the callback's responsibility to apply the passed transformation (or not).

  shapePathMoveTo(path: ShapePath, transformation: Mat3, x: number, y: number): void;
  shapePathLineTo(path: ShapePath, transformation: Mat3, x: number, y: number): void;
  shapePathQuadraticCurveTo(
    path: ShapePath,
    transformation: Mat3,
    aCPx: number,
    aCPy: number,
    ax: number,
    ay: number,
  ): void;
  shapePathBezierCurveTo(
    path: ShapePath,
    transformation: Mat3,
    aCP1x: number,
    aCP1y: number,
    aCP2x: number,
    aCP2y: number,
    ax: number,
    ay: number,
  ): void;

  // `textAnchor` can be one of 'start', 'middle', 'end'
  // If null is returned from the callback the ShapePath is not added to the paths
  shapePathText(
    path: ShapePath,
    transformation: Mat3,
    text: string,
    style: SVGPathStyle,
    x: number,
    y: number,
    fontSize: SheetPixels,
    textAnchor: "start" | "middle" | "end",
    verticalAligment: TextVerticalAlignment,
    baselineShift?: string, // https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/baseline-shift
  ): ShapePath | null;

  shapePathGetCurrentPath(path: ShapePath): Path;

  shapePathAddSubpath(path: ShapePath, subPath: Path): void;

  shapePathDone(path: ShapePath, node: HTMLElement, style: SVGPathStyle, svgTransform: Mat3): void;

  // Path related callbacks.
  // The points are passed to callbacks WITHOUT TRANSFORMATION!
  // It is the callback's responsibility to apply the passed transformation (or not).

  pathAutoClose(path: Path | null, close: boolean): void;

  /* https://svgwg.org/svg2-draft/paths.html#PathElement
   * The "closepath" (Z or z) ends the current subpath by connecting it back
   * to its initial point. An automatic straight line is drawn from the current
   * point to the initial point of the current subpath. This path segment may
   * be of zero length.
   */
  pathCloseCurve(path: Path | null, point: Vec2, svgTransform: Mat3): void;

  pathAbsEllipse(
    path: Path,
    transformation: Mat3,
    centerx: number,
    centery: number,
    xRadius: number,
    yRadius: number,
    aStartAngleRad: number,
    aEndAngleRad: number,
    aClockwise?: boolean, // defaults to 'false'
    aRotationRad?: number, // defaults to 0
  ): void;

  pathAbsArc(
    path: Path,
    transformation: Mat3,
    centerx: number,
    centery: number,
    aRadius: number,
    aStartAngleRad: number,
    aEndAngleRad: number,
    aClockwise?: boolean, // defaults to 'false'
  ): void;
}

// Note: we don't support baseline-based vertical alignment because in our three.js renderer
// we historically didn't support baseline-based vertical alignments. We currently convert
// baseline-based alignments to "bottom" based on our historical behaviour.
// Context: https://linear.app/higharc/issue/SDO-848/remove-baseline-text-vertical-alignment
function verticalAlignmentFromDominantBaseline(dominantBaseline: string): TextVerticalAlignment {
  const verticalAlignmentMap: { [key: string]: TextVerticalAlignment } = {
    auto: TextVerticalAlignment.BOTTOM,
    "text-bottom": TextVerticalAlignment.BOTTOM,
    alphabetic: TextVerticalAlignment.BOTTOM,
    ideographic: TextVerticalAlignment.BOTTOM,
    central: TextVerticalAlignment.CENTER,
    middle: TextVerticalAlignment.CENTER,
    mathematical: TextVerticalAlignment.TOP,
    hanging: TextVerticalAlignment.TOP,
  };
  return verticalAlignmentMap[dominantBaseline] ?? TextVerticalAlignment.BOTTOM;
}

class SVGParser<ShapePath, Path> {
  constructor(
    private callbacks: SVGParserCallbacks<ShapePath, Path>,
    // Default dots per inch
    private defaultDPI: DotsPerInch = 90 as DotsPerInch,
    // Accepted units: 'mm', 'cm', 'in', 'pt', 'pc', 'px'
    private defaultUnit: string = "px",
  ) {}

  parse(text: string) {
    const scope = this; // eslint-disable-line @typescript-eslint/no-this-alias

    function parseNode(node: HTMLElement, style: SVGPathStyle) {
      if (node.nodeType !== 1 /*Node.ELEMENT_NODE*/) {
        return;
      }

      const transform = getNodeTransform(node);

      let traverseChildNodes = true;

      let path: ShapePath | null = null;

      switch (node.nodeName) {
        case "svg":
          if (scope.callbacks.setViewBox) {
            const vb = node.getAttribute("viewBox");
            if (vb) {
              const viewBox = vb.split(" ");
              const minX = parseFloat(viewBox[0]);
              const minY = parseFloat(viewBox[1]);
              const vbWidth = parseFloat(viewBox[2]);
              const vbHeight = parseFloat(viewBox[3]);
              scope.callbacks.setViewBox(minX, minY, vbWidth, vbHeight);
            }
          }
          if (scope.callbacks.setSvgWidth) {
            const widthStr = node.getAttribute("width");
            if (widthStr) {
              const width = parseFloatWithUnits(widthStr);
              scope.callbacks.setSvgWidth(width);
            }
          }
          if (scope.callbacks.setSvgHeight) {
            const heightStr = node.getAttribute("height");
            if (heightStr) {
              const height = parseFloatWithUnits(heightStr);
              scope.callbacks.setSvgHeight(height);
            }
          }
          break;

        case "style":
          parseCSSStylesheet(node as HTMLStyleElement);
          break;

        case "g":
          style = parseStyle(node, style);
          break;

        case "path":
          style = parseStyle(node, style);
          if (node.hasAttribute("d")) {
            path = parsePathNode(node as HTMLElement & SVGPathElement);
          }
          break;

        case "rect":
          style = parseStyle(node, style);
          path = parseRectNode(node as HTMLElement & SVGRectElement);
          break;

        case "polygon":
          style = parseStyle(node, style);
          path = parsePolygonNode(node as HTMLElement & SVGPolygonElement);
          break;

        case "polyline":
          style = parseStyle(node, style);
          path = parsePolylineNode(node as HTMLElement & SVGPolylineElement);
          break;

        case "circle":
          style = parseStyle(node, style);
          path = parseCircleNode(node as HTMLElement & SVGCircleElement);
          break;

        case "ellipse":
          style = parseStyle(node, style);
          path = parseEllipseNode(node as HTMLElement & SVGEllipseElement);
          break;

        case "line":
          style = parseStyle(node, style);
          path = parseLineNode(node as HTMLElement & SVGLineElement);
          break;

        case "defs":
          traverseChildNodes = false;
          break;

        // produces bounding box of text
        case "text":
          style = parseStyle(node, style);
          path = parseTextNode(node as HTMLElement & SVGTextElement, style);
          break;

        // produces bounding box of tspan
        case "tspan":
          style = parseStyle(node, style);
          path = parseTSpanNode(node as HTMLElement & SVGTSpanElement, style);
          break;

        case "use":
          const useNode = node as HTMLElement & SVGUseElement;
          style = parseStyle(node, style);
          if (!!useNode.href?.baseVal) {
            const usedNodeId = useNode.href.baseVal.substring(1);
            const viewPort = useNode.viewportElement;
            if (isDocumentFragment(viewPort) || isDocument(viewPort)) {
              const usedNode = viewPort.getElementById(usedNodeId);
              if (usedNode) {
                parseNode(usedNode, style);
              } else {
                //console.warn("SVGToGeom: 'use node' references non-existent node id: " + usedNodeId);
              }
            } else {
              //console.warn("SVGToGeom: 'use node' references non-document viewport element");
            }
          }

          break;

        default:
        // console.log( node );
      }

      if (path) {
        scope.callbacks.shapePathDone(path, node, style, currentTransform);
      }

      if (traverseChildNodes) {
        const nodes = node.childNodes;

        for (let i = 0; i < nodes.length; i++) {
          parseNode(nodes[i] as HTMLElement, style);
        }
      }

      if (transform) {
        transformStack.pop();

        if (transformStack.length > 0) {
          mat3Copy(transformStack[transformStack.length - 1], currentTransform);
        } else {
          mat3Ident(currentTransform);
        }
      }
    }

    function parsePathNode(node: SVGPathElement) {
      const path: ShapePath = scope.callbacks.newShapePath();

      let point: Vec2 = [0, 0];
      const control: Vec2 = [0, 0];

      let initialPoint: Vec2 | null = null;

      const d = node.getAttribute("d");

      // console.log( d );

      const commands = d !== null ? d.match(/[a-df-z][^a-df-z]*/gi) : null;

      if (commands === null) {
        return path;
      }

      for (let i = 0, l = commands.length; i < l; i++) {
        const command = commands[i];

        const type = command.charAt(0);
        const data = command.substr(1).trim();

        switch (type) {
          case "M":
            {
              const numbers = parseFloats(data);
              for (let j = 0, jl = numbers.length; j < jl; j += 2) {
                point[0] = numbers[j + 0];
                point[1] = numbers[j + 1];
                control[0] = point[0];
                control[1] = point[1];

                if (j === 0) {
                  scope.callbacks.shapePathMoveTo(path, currentTransform, point[0], point[1]);
                } else {
                  scope.callbacks.shapePathLineTo(path, currentTransform, point[0], point[1]);
                }
                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "H":
            {
              const numbers = parseFloats(data);

              for (let j = 0, jl = numbers.length; j < jl; j++) {
                point[0] = numbers[j];
                control[0] = point[0];
                control[1] = point[1];
                scope.callbacks.shapePathLineTo(path, currentTransform, point[0], point[1]);

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "V":
            {
              const numbers = parseFloats(data);

              for (let j = 0, jl = numbers.length; j < jl; j++) {
                point[1] = numbers[j];
                control[0] = point[0];
                control[1] = point[1];
                scope.callbacks.shapePathLineTo(path, currentTransform, point[0], point[1]);

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "L":
            {
              const numbers = parseFloats(data);

              for (let j = 0, jl = numbers.length; j < jl; j += 2) {
                point[0] = numbers[j + 0];
                point[1] = numbers[j + 1];
                control[0] = point[0];
                control[1] = point[1];
                scope.callbacks.shapePathLineTo(path, currentTransform, point[0], point[1]);

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "C":
            {
              const numbers = parseFloats(data);

              for (let j = 0, jl = numbers.length; j < jl; j += 6) {
                scope.callbacks.shapePathBezierCurveTo(
                  path,
                  currentTransform,
                  numbers[j + 0],
                  numbers[j + 1],
                  numbers[j + 2],
                  numbers[j + 3],
                  numbers[j + 4],
                  numbers[j + 5],
                );
                control[0] = numbers[j + 2];
                control[1] = numbers[j + 3];
                point[0] = numbers[j + 4];
                point[1] = numbers[j + 5];

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "S":
            {
              const numbers = parseFloats(data);

              for (let j = 0, jl = numbers.length; j < jl; j += 4) {
                scope.callbacks.shapePathBezierCurveTo(
                  path,
                  currentTransform,
                  getReflection(point[0], control[0]),
                  getReflection(point[1], control[1]),
                  numbers[j + 0],
                  numbers[j + 1],
                  numbers[j + 2],
                  numbers[j + 3],
                );
                control[0] = numbers[j + 0];
                control[1] = numbers[j + 1];
                point[0] = numbers[j + 2];
                point[1] = numbers[j + 3];

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "Q":
            {
              const numbers = parseFloats(data);

              for (let j = 0, jl = numbers.length; j < jl; j += 4) {
                scope.callbacks.shapePathQuadraticCurveTo(
                  path,
                  currentTransform,
                  numbers[j + 0],
                  numbers[j + 1],
                  numbers[j + 2],
                  numbers[j + 3],
                );
                control[0] = numbers[j + 0];
                control[1] = numbers[j + 1];
                point[0] = numbers[j + 2];
                point[1] = numbers[j + 3];

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "T":
            {
              const numbers = parseFloats(data);

              for (let j = 0, jl = numbers.length; j < jl; j += 2) {
                const rx = getReflection(point[0], control[0]);
                const ry = getReflection(point[1], control[1]);
                scope.callbacks.shapePathQuadraticCurveTo(
                  path,
                  currentTransform,
                  rx,
                  ry,
                  numbers[j + 0],
                  numbers[j + 1],
                );
                control[0] = rx;
                control[1] = ry;
                point[0] = numbers[j + 0];
                point[1] = numbers[j + 1];

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "A":
            {
              const numbers = parseFloats(data, [3, 4], 7);

              for (let j = 0, jl = numbers.length; j < jl; j += 7) {
                // skip command if start point == end point
                if (numbers[j + 5] === point[0] && numbers[j + 6] === point[1]) {
                  continue;
                }

                const start = vec2Copy(point);
                point[0] = numbers[j + 5];
                point[1] = numbers[j + 6];
                control[0] = point[0];
                control[1] = point[1];
                parseArcCommand(
                  path,
                  numbers[j],
                  numbers[j + 1],
                  numbers[j + 2],
                  numbers[j + 3],
                  numbers[j + 4],
                  start,
                  point,
                );

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "m":
            {
              const numbers = parseFloats(data);

              for (let j = 0, jl = numbers.length; j < jl; j += 2) {
                point[0] += numbers[j + 0];
                point[1] += numbers[j + 1];
                control[0] = point[0];
                control[1] = point[1];

                if (j === 0) {
                  scope.callbacks.shapePathMoveTo(path, currentTransform, point[0], point[1]);
                } else {
                  scope.callbacks.shapePathLineTo(path, currentTransform, point[0], point[1]);
                }

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "h":
            {
              const numbers = parseFloats(data);

              for (let j = 0, jl = numbers.length; j < jl; j++) {
                point[0] += numbers[j];
                control[0] = point[0];
                control[1] = point[1];
                scope.callbacks.shapePathLineTo(path, currentTransform, point[0], point[1]);

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "v":
            {
              const numbers = parseFloats(data);

              for (let j = 0, jl = numbers.length; j < jl; j++) {
                point[1] += numbers[j];
                control[0] = point[0];
                control[1] = point[1];
                scope.callbacks.shapePathLineTo(path, currentTransform, point[0], point[1]);

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "l":
            {
              const numbers = parseFloats(data);

              for (let j = 0, jl = numbers.length; j < jl; j += 2) {
                point[0] += numbers[j + 0];
                point[1] += numbers[j + 1];
                control[0] = point[0];
                control[1] = point[1];
                scope.callbacks.shapePathLineTo(path, currentTransform, point[0], point[1]);

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "c":
            {
              const numbers = parseFloats(data);

              for (let j = 0, jl = numbers.length; j < jl; j += 6) {
                scope.callbacks.shapePathBezierCurveTo(
                  path,
                  currentTransform,
                  point[0] + numbers[j + 0],
                  point[1] + numbers[j + 1],
                  point[0] + numbers[j + 2],
                  point[1] + numbers[j + 3],
                  point[0] + numbers[j + 4],
                  point[1] + numbers[j + 5],
                );
                control[0] = point[0] + numbers[j + 2];
                control[1] = point[1] + numbers[j + 3];
                point[0] += numbers[j + 4];
                point[1] += numbers[j + 5];

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "s":
            {
              const numbers = parseFloats(data);

              for (let j = 0, jl = numbers.length; j < jl; j += 4) {
                scope.callbacks.shapePathBezierCurveTo(
                  path,
                  currentTransform,
                  getReflection(point[0], control[0]),
                  getReflection(point[1], control[1]),
                  point[0] + numbers[j + 0],
                  point[1] + numbers[j + 1],
                  point[0] + numbers[j + 2],
                  point[1] + numbers[j + 3],
                );
                control[0] = point[0] + numbers[j + 0];
                control[1] = point[1] + numbers[j + 1];
                point[0] += numbers[j + 2];
                point[1] += numbers[j + 3];

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "q":
            {
              const numbers = parseFloats(data);

              for (let j = 0, jl = numbers.length; j < jl; j += 4) {
                scope.callbacks.shapePathQuadraticCurveTo(
                  path,
                  currentTransform,
                  point[0] + numbers[j + 0],
                  point[1] + numbers[j + 1],
                  point[0] + numbers[j + 2],
                  point[1] + numbers[j + 3],
                );
                control[0] = point[0] + numbers[j + 0];
                control[1] = point[1] + numbers[j + 1];
                point[0] += numbers[j + 2];
                point[1] += numbers[j + 3];

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "t":
            {
              const numbers = parseFloats(data);

              for (let j = 0, jl = numbers.length; j < jl; j += 2) {
                const rx = getReflection(point[0], control[0]);
                const ry = getReflection(point[1], control[1]);
                scope.callbacks.shapePathQuadraticCurveTo(
                  path,
                  currentTransform,
                  rx,
                  ry,
                  point[0] + numbers[j + 0],
                  point[1] + numbers[j + 1],
                );
                control[0] = rx;
                control[1] = ry;
                point[0] = point[0] + numbers[j + 0];
                point[1] = point[1] + numbers[j + 1];

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "a":
            {
              const numbers = parseFloats(data, [3, 4], 7);

              for (let j = 0, jl = numbers.length; j < jl; j += 7) {
                // skip command if no displacement
                if (numbers[j + 5] === 0 && numbers[j + 6] === 0) {
                  continue;
                }

                const start = vec2Copy(point);
                point[0] += numbers[j + 5];
                point[1] += numbers[j + 6];
                control[0] = point[0];
                control[1] = point[1];
                parseArcCommand(
                  path,
                  numbers[j],
                  numbers[j + 1],
                  numbers[j + 2],
                  numbers[j + 3],
                  numbers[j + 4],
                  start,
                  point,
                );

                if (j === 0 && initialPoint === null) {
                  initialPoint = vec2Copy(point);
                }
              }
            }
            break;

          case "Z":
          case "z":
            const currentPath = scope.callbacks.shapePathGetCurrentPath(path);
            scope.callbacks.pathAutoClose(currentPath, true);

            if (initialPoint !== null) {
              // Reset point to beginning of Path
              point = vec2Copy(initialPoint);
              scope.callbacks.pathCloseCurve(currentPath, point, currentTransform);
            }

            break;

          default:
          //console.warn(command);
        }

        // console.log( type, parseFloats( data ), parseFloats( data ).length  )
      }

      return path;
    }

    function parseCSSStylesheet(node: HTMLStyleElement) {
      if (!node.sheet || !node.sheet.cssRules || !node.sheet.cssRules.length) {
        return;
      }

      function isCSSStyleRule(r: CSSRule): r is CSSStyleRule {
        return r.type === 1;
      }

      for (let i = 0; i < node.sheet.cssRules.length; i++) {
        const stylesheet: CSSRule = node.sheet.cssRules[i];

        if (!isCSSStyleRule(stylesheet)) {
          continue;
        }

        const selectorList = stylesheet.selectorText
          .split(/,/gm)
          .filter(Boolean)
          .map((i) => i.trim());

        for (let j = 0; j < selectorList.length; j++) {
          stylesheets[selectorList[j]] = Object.assign(stylesheets[selectorList[j]] || {}, stylesheet.style);
        }
      }
    }

    /**
     * https://www.w3.org/TR/SVG/implnote.html#ArcImplementationNotes
     * https://mortoray.com/2017/02/16/rendering-an-svg-elliptical-arc-as-bezier-curves/ Appendix: Endpoint to center arc conversion
     * From
     * rx ry x-axis-rotation large-arc-flag sweep-flag x y
     * To
     * aX, aY, xRadius, yRadius, aStartAngle, aEndAngle, aClockwise, aRotation
     */

    function parseArcCommand(
      path: ShapePath,
      rx: number,
      ry: number,
      x_axis_rotation: number,
      large_arc_flag: number,
      sweep_flag: number,
      start: Vec2,
      end: Vec2,
    ) {
      if (rx === 0 || ry === 0) {
        // draw a line if either of the radii == 0
        scope.callbacks.shapePathLineTo(path, currentTransform, end[0], end[1]);
        return;
      }

      x_axis_rotation = (x_axis_rotation * Math.PI) / 180;

      // Ensure radii are positive
      rx = Math.abs(rx);
      ry = Math.abs(ry);

      // Compute (x1', y1')
      const dx2 = (start[0] - end[0]) / 2.0;
      const dy2 = (start[1] - end[1]) / 2.0;
      const x1p = Math.cos(x_axis_rotation) * dx2 + Math.sin(x_axis_rotation) * dy2;
      const y1p = -Math.sin(x_axis_rotation) * dx2 + Math.cos(x_axis_rotation) * dy2;

      // Compute (cx', cy')
      let rxs = rx * rx;
      let rys = ry * ry;
      const x1ps = x1p * x1p;
      const y1ps = y1p * y1p;

      // Ensure radii are large enough
      const cr = x1ps / rxs + y1ps / rys;

      if (cr > 1) {
        // scale up rx,ry equally so cr == 1
        const s = Math.sqrt(cr);
        rx = s * rx;
        ry = s * ry;
        rxs = rx * rx;
        rys = ry * ry;
      }

      const dq = rxs * y1ps + rys * x1ps;
      const pq = (rxs * rys - dq) / dq;
      const q = Math.sqrt(Math.max(0, pq)) * (large_arc_flag === sweep_flag ? -1 : 1);
      const cxp = (q * rx * y1p) / ry;
      const cyp = (-q * ry * x1p) / rx;

      // Step 3: Compute (cx, cy) from (cx', cy')
      const cx = Math.cos(x_axis_rotation) * cxp - Math.sin(x_axis_rotation) * cyp + (start[0] + end[0]) / 2;
      const cy = Math.sin(x_axis_rotation) * cxp + Math.cos(x_axis_rotation) * cyp + (start[1] + end[1]) / 2;

      // Step 4: Compute θ1 and Δθ
      const theta = svgAngle(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
      const delta = svgAngle((x1p - cxp) / rx, (y1p - cyp) / ry, (-x1p - cxp) / rx, (-y1p - cyp) / ry) % (Math.PI * 2);

      const currentPath = scope.callbacks.shapePathGetCurrentPath(path);
      const rotation = x_axis_rotation;
      scope.callbacks.pathAbsEllipse(
        currentPath,
        currentTransform,
        cx,
        cy,
        rx,
        ry,
        theta,
        theta + delta,
        sweep_flag === 0,
        rotation,
      );
    }

    function svgAngle(ux: number, uy: number, vx: number, vy: number) {
      const dot = ux * vx + uy * vy;
      const len = Math.sqrt(ux * ux + uy * uy) * Math.sqrt(vx * vx + vy * vy);
      let ang = Math.acos(Math.max(-1, Math.min(1, dot / len))); // floating point precision, slightly over values appear
      if (ux * vy - uy * vx < 0) {
        ang = -ang;
      }
      return ang;
    }

    /*
     * According to https://www.w3.org/TR/SVG/shapes.html#RectElementRXAttribute
     * rounded corner should be rendered to elliptical arc, but bezier curve does the job well enough
     */
    function parseRectNode(node: SVGRectElement) {
      const x = parseFloatWithUnits(node.getAttribute("x") || "0");
      const y = parseFloatWithUnits(node.getAttribute("y") || "0");
      let rxRaw = node.getAttribute("rx");
      let ryRaw = node.getAttribute("ry");
      if (!rxRaw && !!ryRaw) {
        rxRaw = ryRaw;
      }
      if (!ryRaw && !!rxRaw) {
        ryRaw = rxRaw;
      }
      const rx = parseFloatWithUnits(rxRaw || "0");
      const ry = parseFloatWithUnits(ryRaw || "0");
      const w = parseFloatWithUnits(node.getAttribute("width") || "0");
      const h = parseFloatWithUnits(node.getAttribute("height") || "0");

      const path = scope.callbacks.newShapePath();
      scope.callbacks.shapePathMoveTo(path, currentTransform, x + rx, y);
      scope.callbacks.shapePathLineTo(path, currentTransform, x + w - rx, y);

      if (rx !== 0 || ry !== 0) {
        scope.callbacks.pathAbsEllipse(
          scope.callbacks.shapePathGetCurrentPath(path),
          currentTransform,
          x + w - rx,
          y + ry,
          rx,
          ry,
          -Math.PI / 2,
          0,
        );
      }
      scope.callbacks.shapePathLineTo(path, currentTransform, x + w, y + h - ry);
      if (rx !== 0 || ry !== 0) {
        scope.callbacks.pathAbsEllipse(
          scope.callbacks.shapePathGetCurrentPath(path),
          currentTransform,
          x + w - rx,
          y + h - ry,
          rx,
          ry,
          0,
          Math.PI / 2,
        );
        //        scope.callbacks.shapePathLineTo(path, currentTransform, x + w - rx, y + h);
      }
      scope.callbacks.shapePathLineTo(path, currentTransform, x + rx, y + h);

      if (rx !== 0 || ry !== 0) {
        scope.callbacks.pathAbsEllipse(
          scope.callbacks.shapePathGetCurrentPath(path),
          currentTransform,
          x + rx,
          y + h - ry,
          rx,
          ry,
          Math.PI / 2,
          Math.PI,
        );
        //        scope.callbacks.shapePathLineTo(path, currentTransform, x, y + h - ry);
      }

      scope.callbacks.shapePathLineTo(path, currentTransform, x, y + ry);

      if (rx !== 0 || ry !== 0) {
        scope.callbacks.pathAbsEllipse(
          scope.callbacks.shapePathGetCurrentPath(path),
          currentTransform,
          x + rx,
          y + ry,
          rx,
          ry,
          Math.PI,
          (3 * Math.PI) / 2,
        );
      }

      return path;
    }

    const parsePointsRegex = /(-?[\d.]+([eE]-?\d+)?)[,|\s](-?[\d.]+([eE]-?\d+)?)/g;

    function parsePolygonNode(node: SVGPolygonElement) {
      function iterator(match: string, a: string, _: string, b: string): string {
        const x = parseFloatWithUnits(a);
        const y = parseFloatWithUnits(b);

        if (index === 0) {
          scope.callbacks.shapePathMoveTo(path, currentTransform, x, y);
        } else {
          scope.callbacks.shapePathLineTo(path, currentTransform, x, y);
        }

        index++;
        return match; // we are not really replacing
      }

      const path = scope.callbacks.newShapePath();

      let index = 0;

      (node.getAttribute("points") ?? "").replace(parsePointsRegex, iterator);

      const currentPath = scope.callbacks.shapePathGetCurrentPath(path);
      scope.callbacks.pathAutoClose(currentPath, true);

      return path;
    }

    function parseTextNode(node: SVGTextElement, style: SVGPathStyle): ShapePath | null {
      if (node?.childNodes[0]?.nodeName === "tspan") {
        return null;
      }

      const text = node.textContent;
      if (text === null) {
        return null;
      }

      const fontSize = parseFloat(node.getAttribute("font-size") || "0") as SheetPixels;
      const x = parseFloatWithUnits(node.getAttribute("x") || "0");
      const y = parseFloatWithUnits(node.getAttribute("y") || "0");
      // Can be one of 'start', 'middle', 'end'
      const textAnchor = node.getAttribute("text-anchor") || "start";
      const dominantBaseline = node.getAttribute("dominant-baseline") || "auto";
      const verticalAlignment = verticalAlignmentFromDominantBaseline(dominantBaseline);
      const baselineShift = node.getAttribute("baseline-shift") ?? "";

      assertIsOneOf<string, "start" | "middle" | "end">(textAnchor, ["start", "middle", "end"]);

      const path = scope.callbacks.newShapePath();
      return scope.callbacks.shapePathText(
        path,
        currentTransform,
        text,
        style,
        x,
        y,
        fontSize,
        textAnchor,
        verticalAlignment,
        baselineShift,
      );
    }

    function parseTSpanNode(node: SVGTSpanElement, style: SVGPathStyle): ShapePath | null {
      const childNode = node.childNodes[0];
      if (!childNode) {
        return null;
      }

      const text = node.textContent;
      if (text === null) {
        return null;
      }

      const dy = parseFloatWithUnits(node.getAttribute("dy") || "0");

      const parentNode = node.parentNode as SVGTextElement;
      const fontSize = parseFloat(parentNode.getAttribute("font-size") || "0") as SheetPixels;
      const x = parseFloatWithUnits(node.getAttribute("x") || "0");
      const y = parseFloatWithUnits(node.getAttribute("y") || "0") + dy;
      const textAnchor = parentNode.getAttribute("text-anchor") || "start";
      const dominantBaseline = node.getAttribute("dominant-baseline") || "auto";
      const verticalAlignment = verticalAlignmentFromDominantBaseline(dominantBaseline);
      const baselineShift = node.getAttribute("baseline-shift") ?? "";

      assertIsOneOf<string, "start" | "middle" | "end">(textAnchor, ["start", "middle", "end"]);

      const path = scope.callbacks.newShapePath();
      return scope.callbacks.shapePathText(
        path,
        currentTransform,
        text,
        style,
        x,
        y,
        fontSize,
        textAnchor,
        verticalAlignment,
        baselineShift,
      );
    }

    function parsePolylineNode(node: SVGPolylineElement) {
      function iterator(match: string, a: string, _: any, b: string): string {
        const x = parseFloatWithUnits(a);
        const y = parseFloatWithUnits(b);

        if (index === 0) {
          scope.callbacks.shapePathMoveTo(path, currentTransform, x, y);
        } else {
          scope.callbacks.shapePathLineTo(path, currentTransform, x, y);
        }

        index++;
        return match; // we are not really replacing
      }

      const path = scope.callbacks.newShapePath();

      let index = 0;

      (node.getAttribute("points") ?? "").replace(parsePointsRegex, iterator);

      const currentPath = scope.callbacks.shapePathGetCurrentPath(path);
      scope.callbacks.pathAutoClose(currentPath, false);

      return path;
    }

    function parseCircleNode(node: SVGCircleElement) {
      const x = parseFloatWithUnits(node.getAttribute("cx") || "0");
      const y = parseFloatWithUnits(node.getAttribute("cy") || "0");
      const r = parseFloatWithUnits(node.getAttribute("r") || "0");

      const path = scope.callbacks.newShapePath();
      const subpath = scope.callbacks.newPath();

      scope.callbacks.pathAbsArc(subpath, currentTransform, x, y, r, 0, Math.PI * 2);

      scope.callbacks.shapePathAddSubpath(path, subpath);

      return path;
    }

    function parseEllipseNode(node: SVGEllipseElement) {
      const x = parseFloatWithUnits(node.getAttribute("cx") || "0");
      const y = parseFloatWithUnits(node.getAttribute("cy") || "0");
      const rx = parseFloatWithUnits(node.getAttribute("rx") || "0");
      const ry = parseFloatWithUnits(node.getAttribute("ry") || "0");

      const path = scope.callbacks.newShapePath();
      const subpath = scope.callbacks.newPath();

      scope.callbacks.pathAbsEllipse(subpath, currentTransform, x, y, rx, ry, 0, Math.PI * 2, false, 0);

      scope.callbacks.shapePathAddSubpath(path, subpath);

      return path;
    }

    function parseLineNode(node: SVGLineElement) {
      const x1 = parseFloatWithUnits(node.getAttribute("x1") || "0");
      const y1 = parseFloatWithUnits(node.getAttribute("y1") || "0");
      const x2 = parseFloatWithUnits(node.getAttribute("x2") || "0");
      const y2 = parseFloatWithUnits(node.getAttribute("y2") || "0");

      const path = scope.callbacks.newShapePath();
      scope.callbacks.shapePathMoveTo(path, currentTransform, x1, y1);
      scope.callbacks.shapePathLineTo(path, currentTransform, x2, y2);
      const currentPath = scope.callbacks.shapePathGetCurrentPath(path);
      scope.callbacks.pathAutoClose(currentPath, false);

      return path;
    }

    function parseStyle(node: HTMLElement, style: SVGPathStyle): SVGPathStyle {
      style = Object.assign({}, style); // clone style

      let stylesheetStyles = {};

      if (node.hasAttribute("class")) {
        const classSelectors = (node.getAttribute("class") ?? "")
          .split(/\s/)
          .filter(Boolean)
          .map((i) => i.trim());

        for (let i = 0; i < classSelectors.length; i++) {
          stylesheetStyles = Object.assign(stylesheetStyles, stylesheets["." + classSelectors[i]]);
        }
      }

      if (node.hasAttribute("id")) {
        stylesheetStyles = Object.assign(stylesheetStyles, stylesheets["#" + node.getAttribute("id")]);
      }

      function addStyle(svgName: string, jsName: keyof SVGPathStyle, adjustFunction?: (v: string) => any) {
        if (adjustFunction === undefined) {
          adjustFunction = function copy(v) {
            //if (v.startsWith("url")) console.warn("SVGParser: url access in attributes is not implemented.");

            return v;
          };
        }

        if (node.hasAttribute(svgName)) {
          style[jsName] = adjustFunction(node.getAttribute(svgName) ?? "");
        }
        if (stylesheetStyles[svgName as keyof typeof stylesheetStyles]) {
          style[jsName] = adjustFunction(stylesheetStyles[svgName as keyof typeof stylesheetStyles]);
        }
        if (node.style && (node.style as unknown as Record<string, string>)[svgName] !== "") {
          style[jsName] = adjustFunction((node.style as unknown as Record<string, string>)[svgName]);
        }
      }

      function clamp(v: string) {
        return Math.max(0, Math.min(1, parseFloatWithUnits(v)));
      }

      function positive(v: string) {
        return Math.max(0, parseFloatWithUnits(v));
      }

      function dashArray(v: string) {
        const arr = v.split(" ").map((x) => parseFloat(x));
        if (arr.length % 2 === 1) {
          // https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/stroke-dasharray
          // "If an odd number of values is provided, then the list of values is repeated to yield an even number of values."
          arr.length = arr.length * 2;
          arr.copyWithin(arr.length, 0, arr.length);
        }
        return arr;
      }

      addStyle("fill", "fill");
      addStyle("fill-opacity", "fillOpacity", clamp);
      addStyle("opacity", "opacity", clamp);
      addStyle("stroke", "stroke");
      addStyle("stroke-opacity", "strokeOpacity", clamp);
      addStyle("stroke-width", "strokeWidth", positive);
      addStyle("stroke-linejoin", "strokeLineJoin");
      addStyle("stroke-linecap", "strokeLineCap");
      addStyle("stroke-miterlimit", "strokeMiterLimit", positive);
      addStyle("stroke-dasharray", "strokeDashArray", dashArray);
      addStyle("visibility", "visibility");

      if (scope.callbacks.setStyle) {
        scope.callbacks.setStyle(style);
      }

      return style;
    }

    // http://www.w3.org/TR/SVG11/implnote.html#PathElementImplementationNotes

    function getReflection(a: number, b: number) {
      return a - (b - a);
    }

    // from https://github.com/ppvg/svg-numbers (MIT License)

    function parseFloats(input: string, flags?: number[], stride?: number) {
      // Character groups
      const RE = {
        SEPARATOR: /[ \t\r\n,.\-+]/,
        WHITESPACE: /[ \t\r\n]/,
        DIGIT: /[\d]/,
        SIGN: /[-+]/,
        POINT: /\./,
        COMMA: /,/,
        EXP: /e/i,
        FLAGS: /[01]/,
        N: /N/,
        a: /a/,
      };

      // States
      const SEP = 0;
      const INT = 1;
      const FLOAT = 2;
      const EXP = 3;
      const NAN = 4;

      let state = SEP;
      let seenComma = true;
      const result: number[] = [];
      let number: string = "";
      let exponent: string = "";

      function throwSyntaxError(current: string, i: number, partial: number[]) {
        const error: SyntaxError & { partial?: number[] } = new SyntaxError(
          'Unexpected character "' + current + '" at index ' + i + ".",
        );
        error.partial = partial;
        throw error;
      }

      function newNumber() {
        if (number !== "") {
          if (exponent === "") {
            result.push(Number(number));
          } else {
            result.push(Number(number) * Math.pow(10, Number(exponent)));
          }
        }

        number = "";
        exponent = "";
      }

      const length = input.length;
      for (let i = 0; i < length; i++) {
        const current = input[i];

        // check for flags
        if (Array.isArray(flags) && stride && flags.includes(result.length % stride) && RE.FLAGS.test(current)) {
          state = INT;
          number = current;
          newNumber();
          continue;
        }

        // parse until next number
        if (state === SEP) {
          // eat whitespace
          if (RE.WHITESPACE.test(current)) {
            continue;
          }

          // start new number
          if (RE.DIGIT.test(current) || RE.SIGN.test(current)) {
            state = INT;
            number = current;
            continue;
          }

          if (RE.POINT.test(current)) {
            state = FLOAT;
            number = current;
            continue;
          }

          // throw on double commas (e.g. "1, , 2")
          if (RE.COMMA.test(current)) {
            if (seenComma) {
              throwSyntaxError(current, i, result);
            }

            seenComma = true;
          }

          if (RE.N.test(current)) {
            // Number starting with N - most likely NaN (not a number, result of 0 divided by 0).
            // NaNs are not part of the SVG spec, but not throwing on NaNs is consistent with parseFloat() that's used to parse some other SVG attributes and can make error conditions less severe.
            state = NAN;
            number = current;
            continue;
          }
        }

        // parse integer part
        if (state === INT) {
          if (RE.DIGIT.test(current)) {
            number += current;
            continue;
          }

          if (RE.POINT.test(current)) {
            number += current;
            state = FLOAT;
            continue;
          }

          if (RE.EXP.test(current)) {
            state = EXP;
            continue;
          }

          // throw on double signs ("-+1"), but not on sign as separator ("-1-2")
          if (RE.SIGN.test(current) && number.length === 1 && RE.SIGN.test(number[0])) {
            throwSyntaxError(current, i, result);
          }
        }

        // parse decimal part
        if (state === FLOAT) {
          if (RE.DIGIT.test(current)) {
            number += current;
            continue;
          }

          if (RE.EXP.test(current)) {
            state = EXP;
            continue;
          }

          // throw on double decimal points (e.g. "1..2")
          if (RE.POINT.test(current) && number[number.length - 1] === ".") {
            throwSyntaxError(current, i, result);
          }
        }

        // parse exponent part
        if (state === EXP) {
          if (RE.DIGIT.test(current)) {
            exponent += current;
            continue;
          }

          if (RE.SIGN.test(current)) {
            if (exponent === "") {
              exponent += current;
              continue;
            }

            if (exponent.length === 1 && RE.SIGN.test(exponent)) {
              throwSyntaxError(current, i, result);
            }
          }
        }

        // parse NaN
        if (state === NAN) {
          if (number.length === 1 && RE.a.test(current)) {
            number += current;
            continue;
          } else if (number.length === 2 && RE.N.test(current)) {
            number += current;
            continue;
          }
        }

        // end of number
        if (RE.WHITESPACE.test(current)) {
          newNumber();
          state = SEP;
          seenComma = false;
        } else if (RE.COMMA.test(current)) {
          newNumber();
          state = SEP;
          seenComma = true;
        } else if (RE.SIGN.test(current)) {
          newNumber();
          state = INT;
          number = current;
        } else if (RE.POINT.test(current)) {
          newNumber();
          state = FLOAT;
          number = current;
        } else {
          throwSyntaxError(current, i, result);
        }
      }

      // add the last number found (if any)
      newNumber();

      return result;
    }

    // Units

    const units = ["mm", "cm", "in", "pt", "pc", "px"];

    // Conversion: [ fromUnit ][ toUnit ] (-1 means dpi dependent)
    const unitConversion: Record<string, Record<string, number>> = {
      mm: {
        mm: 1,
        cm: 0.1,
        in: 1 / 25.4,
        pt: 72 / 25.4,
        pc: 6 / 25.4,
        px: -1,
      },
      cm: {
        mm: 10,
        cm: 1,
        in: 1 / 2.54,
        pt: 72 / 2.54,
        pc: 6 / 2.54,
        px: -1,
      },
      in: {
        mm: 25.4,
        cm: 2.54,
        in: 1,
        pt: 72,
        pc: 6,
        px: -1,
      },
      pt: {
        mm: 25.4 / 72,
        cm: 2.54 / 72,
        in: 1 / 72,
        pt: 1,
        pc: 6 / 72,
        px: -1,
      },
      pc: {
        mm: 25.4 / 6,
        cm: 2.54 / 6,
        in: 1 / 6,
        pt: 72 / 6,
        pc: 1,
        px: -1,
      },
      px: {
        px: 1,
      },
    };

    function parseFloatWithUnits(s: string) {
      let theUnit = "px";

      for (let i = 0, n = units.length; i < n; i++) {
        const u = units[i];

        if (s.endsWith(u)) {
          theUnit = u;
          s = s.substring(0, s.length - u.length);
          break;
        }
      }

      let scale: number;

      if (theUnit === "px" && scope.defaultUnit !== "px") {
        // Conversion scale from  pixels to inches, then to default units

        scale = (unitConversion["in"][scope.defaultUnit] / scope.defaultDPI) as number;
      } else {
        scale = unitConversion[theUnit][scope.defaultUnit];

        if (scale < 0) {
          // Conversion scale to pixels

          scale = unitConversion[theUnit]["in"] * scope.defaultDPI;
        }
      }

      return sheetPixelsNew(scale * parseFloat(s));
    }

    // Transforms

    function getNodeTransform(node: HTMLElement) {
      if (
        !(
          node.hasAttribute("transform") ||
          (node.nodeName === "use" && (node.hasAttribute("x") || node.hasAttribute("y")))
        )
      ) {
        return null;
      }

      const transform: Mat3 = parseNodeTransform(node);

      if (transformStack.length) {
        mat3Mult(transformStack[transformStack.length - 1], transform, transform);
      }

      mat3Copy(transform, currentTransform);
      transformStack.push(transform);

      return transform;
    }

    function parseNodeTransform(node: HTMLElement) {
      const transform: Mat3 = mat3Ident();

      if (node.nodeName === "use" && (node.hasAttribute("x") || node.hasAttribute("y"))) {
        const tx = parseFloatWithUnits(node.getAttribute("x") ?? "0");
        const ty = parseFloatWithUnits(node.getAttribute("y") ?? "0");

        mat3Translate(tx, ty, transform);
      }

      if (node.hasAttribute("transform")) {
        const transformText = node.getAttribute("transform") as string;
        const transformsTexts = transformText.split(")");

        for (let tIndex = transformsTexts.length - 1; tIndex >= 0; tIndex--) {
          const transformText = transformsTexts[tIndex].trim();

          if (transformText === "") {
            continue;
          }

          const openParPos = transformText.indexOf("(");
          const closeParPos = transformText.length;

          mat3Ident(parsedTransform);

          if (openParPos > 0 && openParPos < closeParPos) {
            const transformType = transformText.substr(0, openParPos);

            const array = parseFloats(transformText.substr(openParPos + 1, closeParPos - openParPos - 1));

            switch (transformType) {
              case "translate":
                if (array.length >= 1) {
                  const tx = array[0];
                  const ty = array.length >= 2 ? array[1] : tx;

                  mat3Translate(tx, ty, parsedTransform);
                }

                break;

              case "rotate":
                if (array.length) {
                  const cx = array.length >= 3 ? array[1] : 0;
                  const cy = array.length >= 3 ? array[2] : 0;

                  mat3CSSRotate(array[0], cx, cy, parsedTransform);
                }

                break;

              case "scale":
                if (array.length) {
                  const scaleX = array[0];
                  const scaleY = array.length >= 2 ? array[1] : scaleX;

                  mat3Scale(scaleX, scaleY, parsedTransform);
                }

                break;

              case "skewX":
                if (array.length === 1) {
                  mat3Copy([1, Math.tan((array[0] * Math.PI) / 180), 0, 0, 1, 0, 0, 0, 1], parsedTransform);
                }

                break;

              case "skewY":
                if (array.length === 1) {
                  mat3Copy([1, 0, 0, Math.tan((array[0] * Math.PI) / 180), 1, 0, 0, 0, 1], parsedTransform);
                }

                break;

              case "matrix":
                if (array.length === 6) {
                  mat3Copy([array[0], array[1], 0, array[2], array[3], 0, array[4], array[5], 1], parsedTransform);
                }

                break;
            }
          }

          mat3Mult(parsedTransform, transform, transform);
        }
      }

      return transform;
    }

    const stylesheets: Record<string, SVGPathStyle> = {};

    const transformStack: Mat3[] = [];

    const parsedTransform: Mat3 = mat3Ident();

    const currentTransform: Mat3 = mat3Ident();

    const xml: Document = new ServerSideDOMParser().parseFromString(text, "image/svg+xml");

    if (!xml) {
      throw new Error("SVGParser: Unable to parse SVG.");
    }

    parseNode(xml.documentElement, {
      fill: "black",
      fillOpacity: 1,
      stroke: "none",
      strokeOpacity: 1,
      strokeWidth: sheetPixelsNew(1),
      strokeLineJoin: "miter",
      strokeLineCap: "butt",
      strokeMiterLimit: 4,
    });

    return xml.documentElement;
  }
}

export { SVGParser };
